import {
  wordBreak as wordBreakStyle,
  errorStyle,
  fontStyle,
  fontWeight,
  maxLinesCSS,
  maxLinesVar,
  p,
} from './text.css';
import { clsx } from 'clsx';
import { assignInlineVars } from '@vanilla-extract/dynamic';

// Base props shared by all Text components regardless of the element type
type BaseTextProps = {
  variant?:
    | 'error'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'subtitle1'
    | 'subtitle2'
    | 'subtitle4'
    | 'body1'
    | 'caption1'
    | 'caption2'
    | 'numeric';
  weight?: 'medium' | 'bold';
  maxLines?: number;
  error?: boolean;
  css?: React.CSSProperties;
  children?: React.ReactNode;
  className?: string;
  wordBreak?: boolean;
};

// Polymorphic component type that accepts props based on the element type
export type TextProps<E extends React.ElementType = 'div'> = BaseTextProps & {
  // Allow specifying custom element type
  as?: E;
} & Omit<React.ComponentPropsWithoutRef<E>, keyof BaseTextProps | 'as'>;

// Define the component with generic type parameter
export const Text = <E extends React.ElementType = 'div'>({
  maxLines,
  css,
  variant,
  weight,
  error,
  className,
  wordBreak,
  as,
  ...props
}: TextProps<E>) => {
  const Comp = (as ||
    (typeof variant === 'string' && variant.match(/h[123]/) ? variant : false) ||
    'div') as React.ElementType;

  const fontStyleProp = variant && fontStyle[variant];
  const weightStyleProp = weight && fontWeight[weight];

  return (
    <Comp
      data-component="Text"
      className={clsx(
        wordBreak && wordBreakStyle,
        error && errorStyle,
        maxLines && maxLinesCSS,
        fontStyleProp,
        weightStyleProp,
        className,
      )}
      style={{
        ...(maxLines && assignInlineVars({ [maxLinesVar]: maxLines.toString() })),
        ...css,
      }}
      {...props}
    />
  );
};

export const P = (
  props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLParagraphElement>, HTMLParagraphElement>,
) => <p className={p} {...props} />;
